import React from 'react';

import { Box } from '@mui/material';

// Parse in an inert template and construct React elements ourselves. No HTML or
// attributes from progress messages are ever inserted into the live document.
function parseProgress(text: string): Node[] | undefined {
	if (!/<(?:code|strong|em|br)\s*\/?\s*>/i.test(text)) {
		return undefined;
	}
	const template = document.createElement('template');
	template.innerHTML = text;
	return Array.from(template.content.childNodes);
}

function renderNode(node: Node, key: number): React.ReactNode {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent;
	}
	if (node.nodeType !== Node.ELEMENT_NODE) {
		return null;
	}
	const element = node as Element;
	const tag = element.tagName.toLowerCase();
	if (['code', 'strong', 'em', 'br'].includes(tag)) {
		return React.createElement(tag, { key }, ...(tag === 'br' ? [] : Array.from(node.childNodes).map(renderNode)));
	}
	return element.outerHTML;
}

function nodeText(node: Node): string {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent || '';
	}
	if (node.nodeType !== Node.ELEMENT_NODE) {
		return '';
	}
	const element = node as Element;
	const tag = element.tagName.toLowerCase();
	if (tag === 'br') {
		return '\n';
	}
	return ['code', 'strong', 'em'].includes(tag) ? Array.from(node.childNodes).map(nodeText).join('') : element.outerHTML;
}

export function releasePlanProgressToPlainText(text: string): string {
	return parseProgress(text)?.map(nodeText).join('') ?? text;
}

export default function ReleasePlanProgressText({ text }: { text: string }) {
	return (
		<Box
			component="span"
			sx={{
				overflowWrap: 'anywhere',
				'& code': {
					fontFamily: 'inherit',
					fontSize: 'inherit',
					fontWeight: 600,
					color: 'text.primary',
					bgcolor: 'action.hover',
					borderRadius: 0.5,
					px: 0.35,
					boxDecorationBreak: 'clone',
				},
			}}
		>
			{parseProgress(text)?.map(renderNode) ?? text}
		</Box>
	);
}
